import { useQuery } from '@tanstack/react-query'
import { useQueryState } from 'nuqs'
import { useLocation, useParams } from 'react-router-dom'

import { getScansQueryOptions, getStoreDaysQueryOptions } from '@state/scans'
import { useViewSettings } from '@state/viewSettings'

export const LATEST_DAY = 'latest'

// the pages with a day version, on the latest scan; a specific scan in the URL stays a scan view
const DAY_VIEW_PAGES = /\/scan\/latest\/(store-map|product-finder|out-of-shelf-list)\/?$/
const TODO_PAGE = /\/out-of-shelf-list\/?$/
const LATEST_SCAN_PAGE = /\/scan\/latest(\/|$)/

export function resolveSelectedDay(param: string | null, pathname: string, dayViewIsDefault: boolean) {
  // todos are work to do now, so a picked day doesn't change them; it stays in the URL for the next page
  if (param) return TODO_PAGE.test(pathname) ? LATEST_DAY : param
  return dayViewIsDefault && DAY_VIEW_PAGES.test(pathname) ? LATEST_DAY : undefined
}

/** Todos are work to do now, so their day doesn't depend on the page. A specific scan keeps its own list. */
export function resolveTodoDay(day: string | undefined, pathname: string, dayViewIsDefault: boolean) {
  return day || (dayViewIsDefault && LATEST_SCAN_PAGE.test(pathname)) ? LATEST_DAY : undefined
}

export function isTodoPage(pathname: string) {
  return TODO_PAGE.test(pathname)
}

/**
 * The one scan shown instead of the day: only inside a day view, never on the todo page.
 * It has to belong to the store in the URL: right after a store switch `?scan=` still holds the old store's scan.
 */
export function resolveDayScan(param: string | null, pathname: string, day: string | undefined, storeScanIds: Set<string>) {
  return param && day && !isTodoPage(pathname) && storeScanIds.has(param) ? param : undefined
}

/** The scans `?scan=` can name. Days reach further back than the scan list, which only has the newest ones. */
function useStoreScanIds(enabled: boolean) {
  const { storeId = '' } = useParams<{ storeId?: string }>()
  const { data: scans = [] } = useQuery({ ...getScansQueryOptions(storeId), enabled: enabled && !!storeId })
  const { data: days = [] } = useQuery({ ...getStoreDaysQueryOptions(storeId), enabled: enabled && !!storeId })

  return new Set([...scans.map(scan => scan.id), ...days.flatMap(day => day.scans.map(scan => scan.id))])
}

export function addDayToLink(url: string, day: string | null) {
  if (!day) return url
  return `${url}${url.includes('?') ? '&' : '?'}day=${encodeURIComponent(day)}`
}

/** Keeps a picked day when moving to another page. */
export function useDayLink() {
  const [day] = useQueryState('day')
  return (url: string) => addDayToLink(url, day)
}

/**
 * The day of the day view: `?day=` (yyyy-MM-dd or "latest"), or the deployment's default. Undefined means scan view.
 * `dayScan` is `?scan=`, one scan shown as the robot saw it. Links only carry the day, so leaving the page ends it.
 */
export function useSelectedDay() {
  const [param, setDay] = useQueryState('day', { history: 'push' })
  const [scanParam, setScan] = useQueryState('scan', { history: 'push' })
  const { pathname } = useLocation()
  const { data: settings } = useViewSettings()
  const storeScanIds = useStoreScanIds(!!scanParam)

  const dayViewIsDefault = settings?.dayViewIsDefault ?? false
  const day = resolveSelectedDay(param, pathname, dayViewIsDefault)

  return {
    day,
    todoDay: resolveTodoDay(day, pathname, dayViewIsDefault),
    dayScan: resolveDayScan(scanParam, pathname, day, storeScanIds),
    setDay: (newDay: string) => {
      void setScan(null)
      void setDay(newDay)
    },
  }
}
